import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'

import { AWXCardStatus } from '@ankor-io/common/types/awx'
import type { Card } from '@ankor-io/common/types/cards'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@/ui-components/Dialog'
import { Link } from '@/ui-components/Link'

type CancelCardModalProps = {
  onClose: () => void
  isOpen: boolean
  cardId: string
  onSuccess?: (updatedCard: Card) => void
}

export const CancelCardModal = ({ onClose, isOpen = false, cardId, onSuccess }: CancelCardModalProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const handleCancelCard = () => {
    const cancelCard = async () => {
      if (!activeOrganizationId || !activeConnectedAccountId) {
        throw new Error('Missing organization or connected account context')
      }

      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/card/${cardId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          body: JSON.stringify({
            status: AWXCardStatus.CLOSED,
          }),
        },
      )

      if (!res.ok) {
        const errorText = await res.text()
        throw new Error(`Failed to cancel card: ${res.status} ${res.statusText} - ${errorText}`)
      }

      // Get the updated card data
      const updatedCard: Card = await res.json()

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(updatedCard)
      }

      // Close the modal after successful cancellation
      onClose()
    }

    toast.promise(cancelCard, {
      loading: 'Cancelling card...',
      success: 'Card cancelled successfully',
      error: 'Failed to cancel card',
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>You're about to cancel this card</span>
          <Button
            plain
            onClick={onClose}
            aria-label="Close"
          >
            <X data-slot="icon" />
          </Button>
        </div>
      </DialogTitle>
      <DialogDescription>
        Once cancelled, the card cannot be replaced and no new transactions can be made. However, any{' '}
        <Link
          className="text-primary-600 dark:text-primary-400"
          target="_blank"
          to="https://help.airwallex.com/hc/en-gb/articles/6122441454351-What-are-Pending-or-Reversed-card-transactions"
        >
          pending transactions
        </Link>{' '}
        may still be processed.
        <br />
        <br />
        Please note that this action is irreversible.
      </DialogDescription>
      <DialogActions>
        <Button
          color="red"
          onClick={handleCancelCard}
        >
          Confirm to cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CancelCardModal
